import React, { useState, useEffect } from 'react';
import { Framework, ApplicabilityQuestion, WizardSession } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { ClipboardList, Building, ArrowLeft, ArrowRight, CheckCircle, XCircle, Sparkles, Loader2 } from 'lucide-react';

interface ComplianceWizardProps {
  framework: Framework;
  onComplete: () => void;
  onCancel: () => void;
}

export const ComplianceWizard: React.FC<ComplianceWizardProps> = ({
  framework,
  onComplete,
  onCancel
}) => {
  const [step, setStep] = useState(0); // 0 = org info, 1+ = questions
  const [questions, setQuestions] = useState<ApplicabilityQuestion[]>([]);
  const [session, setSession] = useState<WizardSession | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Organization form
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [orgSize, setOrgSize] = useState('');
  const [country, setCountry] = useState('');
  const [sector, setSector] = useState('');

  useEffect(() => {
    loadQuestions();
  }, [framework.id]);

  const loadQuestions = async () => {
    const q = await grcService.getWizardQuestions(framework.id);
    setQuestions(q);
  };

  const startSession = async () => {
    const s = await grcService.startWizard(framework.id, {
      organization_name: orgName,
      organization_type: orgType,
      organization_size: orgSize,
      country,
      sector
    });
    if (s) {
      setSession(s);
      setStep(1);
    } else {
      setError('Failed to start wizard session');
    }
  };

  const saveAnswer = async (questionId: number, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    if (session) {
      await grcService.saveWizardAnswer(session.session_id, questionId, value);
    }
  };

  const handleNext = async () => {
    if (step === 0) {
      await startSession();
    } else if (step <= questions.length) {
      setStep(step + 1);
      if (session) {
        await grcService.updateWizardProgress(session.session_id, step + 1);
      }
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleComplete = async () => {
    if (!session) return;
    setIsProcessing(true);
    setError(null);

    try {
      const result = await grcService.completeWizard(session.session_id);
      if (result.success && result.data) {
        onComplete();
      } else {
        // Show actual error message from backend
        const errorMsg = result.error || 'AI assessment failed. Please try again.';
        setError(errorMsg);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setIsProcessing(false);
    }
  };

  const totalSteps = questions.length + 1;
  const progress = Math.round((step / totalSteps) * 100);
  const currentQuestion = step > 0 && step <= questions.length ? questions[step - 1] : null;
  const isLastStep = step === questions.length;

  if (isProcessing) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md text-center shadow-2xl">
          <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            AI Analiza u Toku
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            AI analizira vaše odgovore i određuje primenjive kontrole...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
        <div className="flex items-center gap-3 text-white">
          <ClipboardList className="w-6 h-6" />
          <div>
            <h2 className="text-lg font-semibold">{framework.name}</h2>
            <p className="text-sm text-indigo-100">Applicability Assessment Wizard</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm text-white font-medium">
            {step} / {totalSteps}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Step 0: Organization Info */}
        {step === 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Building className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Profil Organizacije
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Unesite osnovne informacije o vašoj organizaciji
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Naziv organizacije
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Vaša organizacija d.o.o."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tip organizacije
                </label>
                <select
                  value={orgType}
                  onChange={e => setOrgType(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Izaberite...</option>
                  <option value="financial">Finansije / Bankarstvo</option>
                  <option value="healthcare">Zdravstvo</option>
                  <option value="tech">Tehnologija / IT</option>
                  <option value="manufacturing">Proizvodnja</option>
                  <option value="government">Vlada / Javni sektor</option>
                  <option value="retail">Maloprodaja / E-commerce</option>
                  <option value="energy">Energetika</option>
                  <option value="telecom">Telekomunikacije</option>
                  <option value="education">Obrazovanje</option>
                  <option value="other">Ostalo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Veličina organizacije
                </label>
                <select
                  value={orgSize}
                  onChange={e => setOrgSize(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Izaberite...</option>
                  <option value="micro">Mikro (1-10 zaposlenih)</option>
                  <option value="small">Malo (11-50 zaposlenih)</option>
                  <option value="medium">Srednje (51-250 zaposlenih)</option>
                  <option value="large">Veliko (251-1000 zaposlenih)</option>
                  <option value="enterprise">Enterprise (1000+ zaposlenih)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Država
                </label>
                <input
                  type="text"
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="Srbija"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sektor industrije
                </label>
                <input
                  type="text"
                  value={sector}
                  onChange={e => setSector(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="npr. Bankarstvo, IT usluge, Proizvodnja..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        {currentQuestion && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-semibold">
                  {step}
                </span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                  {currentQuestion.category}
                </span>
              </div>

              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
                {currentQuestion.question_text}
              </h4>

              {currentQuestion.help_text && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <span className="text-indigo-600 dark:text-indigo-400">ℹ️</span> {currentQuestion.help_text}
                </p>
              )}

              {/* Answer Options */}
              <div className="space-y-3 mt-6">
                {currentQuestion.question_type === 'yes_no' && (
                  <>
                    <button
                      onClick={() => saveAnswer(currentQuestion.id, 'yes')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                        answers[currentQuestion.id] === 'yes'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-600'
                      }`}
                    >
                      <CheckCircle className={`w-5 h-5 ${
                        answers[currentQuestion.id] === 'yes'
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900 dark:text-white">Da</span>
                    </button>
                    <button
                      onClick={() => saveAnswer(currentQuestion.id, 'no')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                        answers[currentQuestion.id] === 'no'
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600'
                      }`}
                    >
                      <XCircle className={`w-5 h-5 ${
                        answers[currentQuestion.id] === 'no'
                          ? 'text-red-600'
                          : 'text-gray-400'
                      }`} />
                      <span className="font-medium text-gray-900 dark:text-white">Ne</span>
                    </button>
                  </>
                )}

                {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
                  <>
                    {(Array.isArray(currentQuestion.options) 
                      ? currentQuestion.options 
                      : JSON.parse(currentQuestion.options as any)
                    ).map((opt: { label: string; value: string }) => (
                      <button
                        key={opt.value}
                        onClick={() => saveAnswer(currentQuestion.id, opt.value)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all text-left ${
                          answers[currentQuestion.id] === opt.value
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          answers[currentQuestion.id] === opt.value
                            ? 'border-indigo-600 bg-indigo-600'
                            : 'border-gray-400'
                        }`}>
                          {answers[currentQuestion.id] === opt.value && (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-white rounded-full" />
                            </div>
                          )}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">{opt.label}</span>
                      </button>
                    ))}
                  </>
                )}

                {currentQuestion.question_type === 'text' && (
                  <textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={e => saveAnswer(currentQuestion.id, e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Unesite vaš odgovor..."
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between">
        <button
          onClick={step === 0 ? onCancel : handlePrev}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 0 ? 'Odustani' : 'Nazad'}
        </button>

        {isLastStep ? (
          <button
            onClick={handleComplete}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
          >
            <Sparkles className="w-4 h-4" />
            Završi & Analiziraj
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-all"
          >
            Dalje
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
